import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, MessageCircle, Eye } from 'lucide-react';
import { SaleOffer, BuyRequest } from '../models/Marketplace';
import { User } from '../models/User';
import { getAllUsers, getDisplayName } from '../data/userDatabase.ts';
import { 
  getPersonDisplayName,
  getPersonId,
  getPrice,
  getQuantity,
  getDate,
  getId,
  calculateTotal,
  formatCurrency
} from '../utils/marketplaceUtils';
import { getModalOverlayClasses, getSecondaryButtonClasses } from '../utils/styleUtils';
import { Z_INDEX } from '../config/constants';

interface ContactModalProps {
  type: 'buyers' | 'sellers';
  isOpen: boolean;
  onClose: () => void;
  sourceItem: SaleOffer | BuyRequest;
  matches: (SaleOffer | BuyRequest)[];
  onContact: (name: string, entityId: string) => void;
}

const ContactModal: React.FC<ContactModalProps> = React.memo(({
  type,
  isOpen,
  onClose,
  sourceItem,
  matches,
  onContact
}) => {
  const [displayNames, setDisplayNames] = useState<{[key: string]: string}>({});
  const [loadingNames, setLoadingNames] = useState<{[key: string]: boolean}>({});
  const [users, setUsers] = useState<User[]>([]);

  // Memoize expensive calculations
  const { getDisplayNameForMatch, getInitialsForMatch, currentConfig } = useMemo(() => {
    const config = {
      buyers: {
        title: 'Contact Buyers',
        theme: 'blue',
        sourceTitle: 'Your Sale Offer',
        sourceLabels: {
          quantity: 'Shares:',
          price: 'Price per Share:',
          total: 'Total Value:'
        },
        emptyTitle: 'No Matching Buyers',
        emptyMessage: 'There are currently no buyers with offers at or above your asking price',
        tableHeaders: {
          person: 'Buyer',
          quantity: 'Shares Wanted',
          price: 'Offer Price',
          total: 'Total Value',
          date: 'Date',
          action: 'Action'
        },
        avatarColor: 'bg-blue-500',
        priceColor: 'bg-green-100 text-green-800'
      },
      sellers: {
        title: 'Contact Sellers',
        theme: 'green',
        sourceTitle: 'Your Buy Request',
        sourceLabels: {
          quantity: 'Shares Wanted:',
          price: 'Max Price per Share:',
          total: 'Max Total Value:'
        },
        emptyTitle: 'No Matching Sellers',
        emptyMessage: 'There are currently no sellers with offers at or below your maximum price',
        tableHeaders: {
          person: 'Seller',
          quantity: 'Shares Available',
          price: 'Ask Price',
          total: 'Total Value',
          date: 'Date',
          action: 'Action'
        },
        avatarColor: 'bg-green-500',
        priceColor: 'bg-blue-100 text-blue-800'
      }
    };

    const currentConfig = config[type];

    const getDisplayNameForMatch = (match: SaleOffer | BuyRequest): string => {
      const matchId = getId(match);
      if (loadingNames[matchId]) {
        return 'Loading...';
      }
      return displayNames[matchId] || getPersonId(match) || 'Unknown User';
    };

    const getInitialsForMatch = (match: SaleOffer | BuyRequest): string => {
      const displayName = getDisplayNameForMatch(match);
      if (displayName === 'Loading...') {
        return '...';
      }
      return displayName.charAt(0).toUpperCase() || 'U';
    };

    return { getDisplayNameForMatch, getInitialsForMatch, currentConfig };
  }, [type, displayNames, loadingNames]);

  // Memoize styling classes
  const overlayClasses = useMemo(() => getModalOverlayClasses(Z_INDEX.MODAL), []);
  const closeButtonClasses = useMemo(() => getSecondaryButtonClasses('sm'), []);

  // Load all users once when modal opens
  useEffect(() => {
    const loadUsers = async () => {
      if (isOpen) {
        try {
          const allUsers = await getAllUsers();
          setUsers(allUsers);
        } catch (error) {
          console.error('Failed to load users:', error);
        }
      }
    };

    loadUsers();
  }, [isOpen]);

  // Optimized display name loading with useCallback
  const loadDisplayNames = useCallback(async () => {
    if (!isOpen || matches.length === 0 || users.length === 0) return;

    const newDisplayNames: {[key: string]: string} = {};
    const newLoadingNames: {[key: string]: boolean} = {};
    
    // Set all matches to loading initially
    matches.forEach(match => {
      const matchId = getId(match);
      newLoadingNames[matchId] = true;
    });
    setLoadingNames(newLoadingNames);
    
    // Process all matches in parallel
    await Promise.all(matches.map(async (match) => {
      const matchId = getId(match);
      const userIdOrUsername = getPersonId(match);
      
      try {
        // Find user by ID
        const user = users.find(u => u.id === userIdOrUsername);
        
        if (user) {
          newDisplayNames[matchId] = getDisplayName(user);
        } else {
          newDisplayNames[matchId] = `User ${userIdOrUsername}`;
        }
      } catch (error) {
        console.error('Failed to get display name for match:', matchId, error);
        newDisplayNames[matchId] = `User ${userIdOrUsername}`;
      } finally {
        newLoadingNames[matchId] = false;
      }
    }));
    
    setDisplayNames(newDisplayNames);
    setLoadingNames(newLoadingNames);
  }, [isOpen, matches, users]);

  useEffect(() => {
    loadDisplayNames();
  }, [loadDisplayNames]);

  if (!isOpen) return null;

  const themeClass = `bg-${currentConfig.theme}-50`;

  return (
    <div className={overlayClasses}>
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-y-auto p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{currentConfig.title}</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className={`mb-4 p-4 ${themeClass} rounded-lg`}>
          <h4 className="text-sm font-medium text-gray-800 mb-2">{currentConfig.sourceTitle}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-sm">
            <div className="flex justify-between sm:block">
              <span className="text-gray-600">{currentConfig.sourceLabels.quantity}</span> 
              <span className="font-medium ml-2 sm:ml-0">{getQuantity(sourceItem)}</span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-gray-600">{currentConfig.sourceLabels.price}</span> 
              <span className="font-medium ml-2 sm:ml-0">{formatCurrency(getPrice(sourceItem))}</span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-gray-600">{currentConfig.sourceLabels.total}</span> 
              <span className="font-medium ml-2 sm:ml-0">{formatCurrency(calculateTotal(getPrice(sourceItem), getQuantity(sourceItem)))}</span>
            </div>
          </div>
        </div>

        {matches.length === 0 ? (
          <div className="text-center py-8">
            <div className="bg-gray-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <MessageCircle className="w-8 h-8 text-gray-400" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">{currentConfig.emptyTitle}</h4>
            <p className="text-gray-600">
              {currentConfig.emptyMessage} of {formatCurrency(getPrice(sourceItem))}.
            </p>
          </div>
        ) : (
          <div>
            <h4 className="text-sm font-medium text-gray-800 mb-3">
              {currentConfig.title.replace('Contact ', '')} with Matching or {type === 'buyers' ? 'Higher' : 'Lower'} Offers ({matches.length})
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200 min-w-[600px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      {currentConfig.tableHeaders.person}
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      {currentConfig.tableHeaders.quantity}
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      {currentConfig.tableHeaders.price}
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      {currentConfig.tableHeaders.total}
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      {currentConfig.tableHeaders.date}
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      {currentConfig.tableHeaders.action}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {matches.map((match) => (
                    <tr key={getId(match)} className="hover:bg-gray-50">
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-7 w-7">
                            <div className={`h-7 w-7 rounded-full ${currentConfig.avatarColor} flex items-center justify-center`}>
                              <span className="text-xs font-medium text-white">
                                {getInitialsForMatch(match)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-2">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-32">{getDisplayNameForMatch(match)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        {getQuantity(match)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${currentConfig.priceColor}`}>
                          {formatCurrency(getPrice(match))}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(calculateTotal(getPrice(match), getQuantity(match)))}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        {getDate(match)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => onContact(getPersonId(match), getId(match))}
                          className={closeButtonClasses}
                        >
                          Contact
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

ContactModal.displayName = 'ContactModal';

export default ContactModal; 